import { useBreakpointsContext } from '@air/provider-media-query';

import {
  useOnboardingPreferencesStore,
  useShouldMountOnboardingIntroValue,
  useShowFirstUploadPromptValue,
} from '~/components/v2/store/features/onboarding/useOnboardingPreferencesStore';
import { usePrivateAddOnFeatures } from '~/hooks/usePrivateAddOnFeatures';
import { useZustandPersistHasHydrated } from '~/hooks/useZustandPersistHasHydrated';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { isCypressRun } from '~/utils/PathUtils';

/**
 * Whether the intro overlay is allowed to mount right now, aside from whether this account
 * still needs to watch it. The review task uses the same gate so opening it cannot clear
 * "watched" while the intro would stay hidden (for example during the first-upload step).
 *
 * `shouldMountOnboardingIntro` is written only on create-workspace. Missing storage is an
 * existing user (including a new browser) and must not show the overlay.
 */
export const useCanMountPrivateOnboardingIntro = (): { canMountPrivateOnboardingIntro: boolean } => {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const { isAboveMediumScreen } = useBreakpointsContext();
  const shouldMountOnboardingIntro = useShouldMountOnboardingIntroValue(workspaceId);
  const { agentSessions: isAgentSessionsEnabled } = usePrivateAddOnFeatures();
  const { hasHydrated } = useZustandPersistHasHydrated(useOnboardingPreferencesStore.persist);
  const isNewUserOnboardingInProgress = useShowFirstUploadPromptValue(workspaceId);

  return {
    canMountPrivateOnboardingIntro:
      hasHydrated &&
      isAboveMediumScreen &&
      shouldMountOnboardingIntro &&
      isAgentSessionsEnabled === true &&
      !isCypressRun() &&
      !!workspaceId &&
      !isNewUserOnboardingInProgress,
  };
};
